/**
 * Virtual shifting controller (SPEC §6.3): turns (cadence, virtual gear, road) into trainer commands.
 * A (default): feed-forward grade solved against an online-estimated trainer model.
 * B (fallback): cadence-driven ERG with target P_req, low-pass filtered.
 */
import type { VsHook, VsInput, VsOutput } from '@/ride/session';
import { totalMassKg } from '@/store/settingsModel';
import { BIKE, GEARS } from '@/physics/bike';
import { airDensity } from '@/physics/constants';
import { defaultGear, gearRatios, physicalRatio } from '@/physics/gearing';
import {
  feltGrade,
  followGrade,
  momentumTauSec,
  plausibleTrainerSpeed,
  solveGrade,
  speedFromCadence,
  vsTargets,
} from '@/physics/vs';
import { TrainerModelEstimator, type EstimatorOptions } from '@/physics/estimator';

export const MIN_CADENCE = 20;
/** Seconds of VS control before the estimator learns (the trainer may still apply older parameters). */
export const ESTIMATOR_WARMUP_SEC = 4;
/** Seconds after a shift during which samples are ignored (transient). */
export const ESTIMATOR_SHIFT_HOLDOFF_SEC = 1.5;
const GRADE_SMOOTH_TAU = 0.35;
const ERG_SMOOTH_TAU = 0.6;

export class VirtualShifter implements VsHook {
  gear: number;
  ratios: number[];
  readonly estimator: TrainerModelEstimator;
  private lastGrade: number | null = null;
  private lastErg: number | null = null;
  private shiftPending = false;
  private last: VsOutput | null = null;
  private controlSec = 0;
  private sinceShiftSec = Infinity;
  /** Road grade after the bike's momentum (%), before the descent factor. */
  private momentumGrade: number | null = null;
  /** Grade the rider currently feels (%), for the debug overlay. */
  feltGradePct = 0;
  /** Ratio of the last shift for the HUD animation. */
  lastShiftAt = 0;

  constructor(estimatorOpts: Partial<EstimatorOptions> = {}) {
    this.ratios = gearRatios(GEARS);
    this.gear = defaultGear(this.ratios, physicalRatio(BIKE.chainringTeeth, BIKE.cogTeeth));
    this.estimator = new TrainerModelEstimator(estimatorOpts);
  }

  get ratio(): number {
    return this.ratios[this.gear - 1]!;
  }

  shift(dir: 1 | -1): void {
    const next = Math.max(1, Math.min(this.ratios.length, this.gear + dir));
    if (next === this.gear) return;
    this.gear = next;
    this.shiftPending = true;
    this.lastShiftAt = Date.now();
  }

  compute(input: VsInput): VsOutput {
    const s = input.settings;
    const rp = physicalRatio(BIKE.chainringTeeth, BIKE.cogTeeth);
    const rv = this.ratio;
    const cad = input.cadenceRpm;
    const mass = totalMassKg(s);
    const cdaEff = BIKE.cda * (1 - input.draft);
    const tau = momentumTauSec(
      mass,
      speedFromCadence(Math.max(0, cad), rv, BIKE.wheelCircumferenceM),
      input.powerW,
      airDensity(input.altitudeM),
      cdaEff,
    );
    this.momentumGrade = followGrade(this.momentumGrade, input.gradePct, input.dt, tau);
    this.feltGradePct = feltGrade(this.momentumGrade);
    if (cad < MIN_CADENCE) {
      // Not pedalling: hold the last command (no meaningful force target).
      return this.last ? { ...this.last, gear: this.gear } : { gear: this.gear };
    }
    const vPhysCad = speedFromCadence(cad, rp, BIKE.wheelCircumferenceM);
    const vPhysMeasured = plausibleTrainerSpeed(input.trainerSpeedKmh, vPhysCad);
    const t = vsTargets({
      cadenceRpm: cad,
      virtualRatio: rv,
      physicalRatio: rp,
      wheelM: BIKE.wheelCircumferenceM,
      rider: { massKg: mass, cda: BIKE.cda },
      road: {
        gradePct: this.feltGradePct,
        difficultyPct: s.trainerDifficultyPct,
        altitudeM: input.altitudeM,
        draft: input.draft,
      },
      ...(vPhysMeasured !== undefined ? { vPhysMeasured } : {}),
    });
    const immediate = this.shiftPending;
    this.shiftPending = false;
    this.controlSec += input.dt;
    this.sinceShiftSec = immediate ? 0 : this.sinceShiftSec + input.dt;

    if (s.vsStrategy === 'B') {
      const target = Math.max(0, t.pReq);
      const erg =
        immediate || this.lastErg === null
          ? target
          : this.lastErg + (target - this.lastErg) * (1 - Math.exp(-input.dt / ERG_SMOOTH_TAU));
      this.lastErg = erg;
      this.last = { ergW: Math.round(erg), pReq: t.pReq, gear: this.gear };
      return this.last;
    }

    // Strategy A — learn the trainer model from what it actually produced for our recent commands.
    if (
      this.controlSec > ESTIMATOR_WARMUP_SEC &&
      this.sinceShiftSec > ESTIMATOR_SHIFT_HOLDOFF_SEC &&
      input.powerW > 20 &&
      cad > 40 &&
      t.vPhys > 2 &&
      this.estimator.informative
    ) {
      this.estimator.update(input.powerW / t.vPhys, t.vPhys);
    }
    const target = solveGrade(t.fTrainer, t.vPhys, this.estimator.model);
    const g =
      immediate || this.lastGrade === null
        ? target
        : this.lastGrade + (target - this.lastGrade) * (1 - Math.exp(-input.dt / GRADE_SMOOTH_TAU));
    this.lastGrade = g;
    this.estimator.trackCommand(g, input.dt, t.vPhys);
    const o = this.estimator.opts;
    this.last = {
      sim: { gradePct: Math.round(g * 100) / 100, crr: o.crrSent, cwKgPerM: o.cwSent, windMps: 0 },
      gSend: g,
      pReq: t.pReq,
      gear: this.gear,
    };
    return this.last;
  }
}
